import {
  isQuotationCurrency,
  upgradeQuotationDraft,
} from '@nadoutong/contracts';
import type {
  BrandId,
  QuotationCurrency,
  QuotationDraftNamespace,
  QuotationDraftPayload,
  QuotationDraftPayloadV1,
  QuotationDraftSku,
  QuotationNamespace,
  QuotationSku,
} from '@nadoutong/contracts';

export type QuotationNamespaceDraft = QuotationDraftNamespace;

export type QuotationDraft = QuotationDraftPayload;

export interface DraftStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function emptyDraft(today: string, currency: QuotationCurrency): QuotationDraft {
  return {
    version: 2,
    currency,
    customer_name: '',
    quote_date: today,
    valid_days: '',
    note: '',
    namespaces: {},
  };
}

export function draftStorageKey(opsUserId: string, brand: string | null): string {
  const brandKey = brand !== null && brand.length > 0 ? brand : 'own';
  return `ops.quotation.draft.${opsUserId}.${brandKey}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isSkuDraft(value: unknown): value is QuotationDraftSku {
  return isRecord(value) && typeof value.discount_zhe === 'string';
}

function isNamespaceV1(
  value: unknown,
): value is { readonly included: boolean; readonly discount_zhe: string } {
  return isRecord(value) && typeof value.included === 'boolean' && typeof value.discount_zhe === 'string';
}

function isNamespaceV2(value: unknown): value is QuotationNamespaceDraft {
  if (!isRecord(value)) return false;
  if (typeof value.included !== 'boolean' || typeof value.discount_zhe !== 'string') return false;
  if (!isRecord(value.skus)) return false;
  return Object.values(value.skus).every(isSkuDraft);
}

function hasDraftMeta(value: Record<string, unknown>): boolean {
  return (
    typeof value.customer_name === 'string' &&
    typeof value.quote_date === 'string' &&
    typeof value.valid_days === 'string' &&
    typeof value.note === 'string' &&
    isRecord(value.namespaces)
  );
}

function namespaceEntries(value: Record<string, unknown>): unknown[] | null {
  const namespaces = value.namespaces;
  if (typeof namespaces !== 'object' || namespaces === null) return null;
  return Object.values(namespaces);
}

function isDraftV1(value: unknown): value is QuotationDraftPayloadV1 {
  if (!isRecord(value) || value.version !== 1 || !hasDraftMeta(value)) return false;
  const namespaces = namespaceEntries(value);
  if (namespaces === null) return false;
  return namespaces.every(isNamespaceV1);
}

function isDraftV2(value: unknown): value is QuotationDraft {
  if (!isRecord(value) || value.version !== 2 || !hasDraftMeta(value)) return false;
  if (!isQuotationCurrency(value.currency)) return false;
  const namespaces = namespaceEntries(value);
  if (namespaces === null) return false;
  return namespaces.every(isNamespaceV2);
}

/** Accepts a stored v1 or v2 body. v1 is upgraded with the brand's default currency. */
export function loadDraft(storage: DraftStorage, key: string, brand: BrandId): QuotationDraft | null {
  const raw = storage.getItem(key);
  if (raw === null || raw.length === 0) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isDraftV2(parsed)) return parsed;
    if (isDraftV1(parsed)) return upgradeQuotationDraft(parsed, brand);
    return null;
  } catch {
    return null;
  }
}

export function saveDraft(storage: DraftStorage, key: string, draft: QuotationDraft): void {
  storage.setItem(key, JSON.stringify(draft));
}

/**
 * Accept 0.1–10 with at most one decimal place. `6` and `5.5` are in;
 * `5.55`, `0`, and `10.1` are out.
 */
export function parseZhe(text: string): number | null {
  const trimmed = text.trim();
  if (!/^(?:10(?:\.0)?|[0-9](?:\.[0-9])?)$/.test(trimmed)) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0.1 || value > 10) return null;
  return value;
}

export function zheToMultiplier(zhe: number): number {
  return zhe / 10;
}

/** Missing draft row: included, with the discount still blank and no SKU overrides. */
export function namespaceDraft(draft: QuotationDraft, slug: string): QuotationNamespaceDraft {
  return draft.namespaces[slug] ?? { included: true, discount_zhe: '', skus: {} };
}

/** Missing SKU row inherits the namespace 折. */
export function skuDraft(entry: QuotationNamespaceDraft, skuId: string): QuotationDraftSku {
  return entry.skus[skuId] ?? { discount_zhe: '' };
}

/**
 * SKU override when its text is non-blank and a valid 折; otherwise the namespace 折.
 * An invalid override falls back instead of blanking the row.
 */
export function effectiveSkuZhe(entry: QuotationNamespaceDraft, skuId: string): number | null {
  const override = entry.skus[skuId];
  if (override !== undefined && override.discount_zhe.trim().length > 0) {
    const parsed = parseZhe(override.discount_zhe);
    if (parsed !== null) return parsed;
  }
  return parseZhe(entry.discount_zhe);
}

/** SKUs whose override text is non-blank, whether or not it parses. */
export function namespaceOverrideCount(entry: QuotationNamespaceDraft): number {
  let count = 0;
  for (const sku of Object.values(entry.skus)) {
    if (sku.discount_zhe.trim().length > 0) count += 1;
  }
  return count;
}

/**
 * Lowest 折 quotable on this SKU. The sales floor is a multiplier on the
 * official price; the typed 折 applies to 挂牌价 = official × rate_multiplier.
 */
export function skuFloorZhe(sku: QuotationSku, namespaceFloor: number | null): number | null {
  const floor = sku.sales_floor_multiplier ?? namespaceFloor;
  if (floor === null) return null;
  return (floor / sku.rate_multiplier) * 10;
}

/** Drop binary noise (…0001 / …9996) before a display ceil or a floor compare. */
function roundFloatNoise(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

/** Highest per-SKU floor in the namespace, rounded up to one decimal. */
export function namespaceFloorZhe(namespace: QuotationNamespace): number | null {
  let max: number | null = null;
  for (const sku of namespace.skus) {
    const zhe = skuFloorZhe(sku, namespace.sales_floor_multiplier);
    if (zhe === null) continue;
    max = max === null ? zhe : Math.max(max, zhe);
  }
  if (max === null) return null;
  return Math.ceil(roundFloatNoise(max) * 10) / 10;
}

/** True when `zhe` undercuts this SKU's floor (`sku.sales_floor_multiplier ?? namespaceFloor`). */
export function skuBelowFloor(sku: QuotationSku, namespaceFloor: number | null, zhe: number): boolean {
  const floor = sku.sales_floor_multiplier ?? namespaceFloor;
  if (floor === null) return false;
  const quoted = roundFloatNoise((sku.rate_multiplier * zhe) / 10);
  return quoted < roundFloatNoise(floor) - 1e-9;
}

/** True when any SKU's effective 折 undercuts its floor. A blank effective 折 never warns. */
export function namespaceBelowFloor(
  namespace: QuotationNamespace,
  entry: QuotationNamespaceDraft,
): boolean {
  for (const sku of namespace.skus) {
    const zhe = effectiveSkuZhe(entry, sku.sku_id);
    if (zhe === null) continue;
    if (skuBelowFloor(sku, namespace.sales_floor_multiplier, zhe)) return true;
  }
  return false;
}
